<script lang="ts">
    import get from 'lodash/get';
    import { RESULT_MAPPING_KEY } from '$lib/global/constants';
    import { createExternalUrl, getColor, getColorByLightness, personalizeText } from '$lib/helper';
    import { onMount } from 'svelte';
    import { NextPageNavigator } from '$lib/global/helper/NextPageNavigator';
    import { page } from '$app/stores';
    import { trackAnswerClick } from '$lib/global/tracking';
    import { DEFAULT_LAYOUT } from '$lib/components/constants.js';

    // Types
    import type { ComponentType } from 'svelte';
    import type { Component } from '$lib/types/Components/SharedTypes';
    import type { Field } from '$lib/types/Field';
    import type { Theme, TransitionEffect } from '$lib/types/Theme';
    import type { ResultMapping } from '$lib/types/ResultMapping';
    import type { TrackingProperty } from '$lib/types/TrackingProperty';
    import type { Language } from '$lib/types/Language';
    import type { TextQuestionContent } from '$lib/types/Components/TextQuestion/TextQuestionContent';

    export let id = '';
    export let content: TextQuestionContent;
    export let childComponents: Component[] = [];
    export let fields: Field[] = [];
    export let theme: Theme;
    export let resultMapping: ResultMapping;
    export let currentPage = '';
    export let nextPage = '';
    export let campaignId = '';
    export let firstResultPage = '';
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'none';
    export let trackingProperties: TrackingProperty[] = [];
    export let language: Language = 'de';
    export let layout = DEFAULT_LAYOUT;

    const {
        data: { pageIdSlugMap },
    } = $page;

    // Navigation
    const nextPageNavigator = new NextPageNavigator(campaignId, firstResultPage, pageIdSlugMap);

    let personalizedText = content.wysiwyg;

    let TextQuestion: ComponentType;

    onMount(async () => {
        TextQuestion = (
            await import('$lib/components/_DisplayComponents/TextQuestion/TextQuestion.svelte')
        ).default;

        personalizedText = personalizeText(
            personalizedText,
            trackingProperties,
            campaignId,
            language,
        );
    });

    const answers = childComponents.map((answer) => {
        const bgColor = getColor(
            get(answer, 'content.backgroundColor'),
            theme.formFieldBackgroundColor,
        );

        return {
            id: answer._id,
            color: getColor(get(answer, 'content.color'), getColorByLightness(bgColor)),
            bgColor,
            emoji: get(answer, 'content.icon', ':smiley:'),
            showIcon: get(answer, 'content.showIcon', false),
            draft: personalizeText(
                answer.content.wysiwyg,
                trackingProperties,
                campaignId,
                language,
            ),
            resultId: get(resultMapping, `${currentPage}.${answer._id}.destination`),
            dynamicNextPage: get(answer, 'content.linking.pageId'),
            link: get(answer, 'content.linking.isInternal', true)
                ? ''
                : get(answer, 'content.linking.url', ''),
        };
    });

    const handleAnswerClick = (resultId: string, answerId: string, link: string) => {
        const ST = window.sessionStorage;
        const resultCounts = JSON.parse(ST.getItem(`${RESULT_MAPPING_KEY}${campaignId}`) || '{}');
        const answer = answers.find((answer) => answer.id === answerId);
        const dynamicNextPage = answer?.dynamicNextPage;

        // Tracking
        trackAnswerClick({
            answer: childComponents.find((child) => child._id === answerId),
            question: { id, content },
            fields,
        });

        if (resultId) {
            // Increment count for result page
            const resultCount = get(resultCounts, resultId, 0) + 1;
            const updatedResultCounts = {
                ...resultCounts,
                [resultId]: resultCount,
            };

            // Save updated result count to session storage
            ST.setItem(`${RESULT_MAPPING_KEY}${campaignId}`, JSON.stringify(updatedResultCounts));
        }

        // Navigate to external page
        if (link) {
            return nextPageNavigator.goExternal(createExternalUrl(link));
        }

        return nextPageNavigator.go(dynamicNextPage || nextPage);
    };

    const handleAnswerMouseEnter = (resultId: string, answerId: string, link: string) => {
        const answer = answers.find((answer) => answer.id === answerId);
        const dynamicNextPage = answer?.dynamicNextPage;

        nextPageNavigator.preload(dynamicNextPage || nextPage, !!link);
    };
</script>

<svelte:component
    this={TextQuestion}
    draft={personalizedText}
    align={content.align}
    color={getColor(content.color, theme.fontColor)}
    box={content.box}
    onAnswerClick={handleAnswerClick}
    onAnswerMouseEnter={handleAnswerMouseEnter}
    {answers}
    {transitionDelay}
    {transitionEffect}
    {layout}
/>
